<script setup>
import { computed } from 'vue';

import Icon from '@/components/foundation/Icon.vue';
import { useIntentMap } from '@/components/VisionAi/composables/useIntentMap';
import {
  isSummaryPromptMessage,
  useSubmitPromptConfig,
} from '@/components/VisionAi/composables/useSubmitPromptConfig';
import UserWrapper from '@/components/VisionAi/UserWrapper.vue';
import { useVisionAiStore } from '@/stores/vision-ai';

const visionAiStore = useVisionAiStore();

const { checkIfInvalidThreshold } = useIntentMap();
const { submitPromptConfig } = useSubmitPromptConfig();

const props = defineProps({
  promptConfig: {
    type: Object,
    default: () => ({}),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
    default: null,
  },
});

const tooltipProps = computed(() => {
  if (props.disabled) {
    return { content: props.tooltip, boundary: 'viewport', placement: 'top' };
  }

  return {
    content: props.tooltip,
    theme: 'dh-tooltip-center',
  };
});

const isSummaryPrompt = computed(() => isSummaryPromptMessage(props.promptConfig.message));

async function handlePromptButtonClicked() {
  if (props.disabled) return;
  await submitPromptConfig(props.promptConfig);
}

const summaryPromptReady = computed(
  () =>
    Boolean(props.promptConfig.intentTypeOverride) ||
    (isSummaryPrompt.value && !checkIfInvalidThreshold(visionAiStore.currentIntentType)),
);
</script>

<template>
  <UserWrapper
    v-if="props.promptConfig.message"
    v-tooltip.top="tooltipProps"
    :summary-ready="summaryPromptReady"
    :is-disabled="disabled"
    @user-button-clicked="handlePromptButtonClicked"
  >
    <div
      class="flex !h-[--space-18] !w-[--space-18] items-center justify-center rounded-md bg-[--brand-primary] p-[--space-3]"
    >
      <Icon name="prompt-icon" :color="'white'" />
    </div>

    <span>{{ props.promptConfig.label || props.promptConfig.message }}</span>
  </UserWrapper>
</template>
